import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { AuthContext, type AuthContextType } from "./AuthContext";
import { authService } from "@hub/api/authService";
import { createTypedClient } from "@hub/api/services";
import {
  setTokens,
  getAccessToken,
  getRefreshToken,
  clearTokens,
  getTokenGeneration,
  AUTH_EXPIRED_EVENT,
} from "@hub/api/client";
import { logError } from "@hub/shared/utils/errorLogger";
import type { User } from "@hub/api/types";
import {
  AuthService,
  AuthProvider as AuthProviderEnum,
} from "@hub/proto/auth/v1/auth_pb";

const authClient = createTypedClient(AuthService);

const VALID_ROLES: ReadonlySet<User["role"]> = new Set([
  "admin",
  "operator",
  "viewer",
]);

function authProviderName(provider: AuthProviderEnum): string {
  switch (provider) {
    case AuthProviderEnum.API_KEY:
      return "api_key";
    case AuthProviderEnum.OIDC:
      return "oidc";
    case AuthProviderEnum.SAML:
      return "saml";
    case AuthProviderEnum.CERTIFICATE:
      return "certificate";
    case AuthProviderEnum.LOCAL:
      return "local";
    default:
      return "unknown";
  }
}

function userFromPrincipal(principal: {
  id: string;
  email?: string;
  name: string;
  roles?: string[];
  authProvider?: AuthProviderEnum;
}): User {
  const rawRole = principal.roles?.[0];
  const role: User["role"] =
    rawRole && VALID_ROLES.has(rawRole as User["role"])
      ? (rawRole as User["role"])
      : "viewer";

  return {
    id: principal.id,
    email: principal.email || "",
    displayName: principal.name,
    role,
    // The server's roles, UNCLAMPED. `role` above collapses them twice — to the
    // first entry, then onto one of three console names — so an IdP group like
    // "auditor" arrives there as "viewer". Anything describing the caller's own
    // scope needs the real list; the clamped value exists for the console's
    // admin/operator gates and nothing else.
    roles: principal.roles ? [...principal.roles] : undefined,
    authProvider: authProviderName(
      principal.authProvider ?? AuthProviderEnum.UNSPECIFIED,
    ),
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshingRef = useRef(false);

  // Attempts to refresh tokens using the stored refresh token, validates the new
  // access token, and updates the user state. Returns the resolved User on success
  // or null on any failure. Discards new tokens if a logout occurred mid-flight.
  const tryRefreshTokens = useCallback(async (): Promise<User | null> => {
    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) {
      return null;
    }
    const gen = getTokenGeneration();
    try {
      const refreshResponse = await authClient.refreshToken({
        refreshToken: currentRefreshToken,
      });
      if (!refreshResponse.accessToken) {
        return null;
      }
      // If logout happened while the refresh was in-flight, discard the new tokens
      if (getTokenGeneration() !== gen) {
        return null;
      }
      setTokens(refreshResponse.accessToken, refreshResponse.refreshToken);
      const validateResponse = await authClient.validateToken({
        token: refreshResponse.accessToken,
      });
      if (validateResponse.valid && validateResponse.principal) {
        const resolvedUser = userFromPrincipal(validateResponse.principal);
        setUser(resolvedUser);
        return resolvedUser;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      // Check for in-memory access token from API key login
      const currentAccessToken = getAccessToken();
      if (currentAccessToken) {
        // Validate the token
        try {
          const response = await authClient.validateToken({
            token: currentAccessToken,
          });
          if (response.valid && response.principal) {
            setUser(userFromPrincipal(response.principal));
            return;
          }
        } catch {
          // Token invalid, try refresh token
          const refreshedUser = await tryRefreshTokens();
          if (refreshedUser) {
            return;
          }
          // Refresh failed, clear tokens
          clearTokens();
        }
      }

      // Fall back to cookie-based refresh
      const result = await authService.refresh();
      if (result.networkError) {
        // Network error — don't clear user state, just stop loading
        // User remains in whatever state they were in
        return;
      }
      if (result.accessToken) {
        setTokens(result.accessToken, ""); // No refresh token in memory for cookie-based flow
      }
      setUser(result.user);
    } catch (err) {
      logError("AuthProvider.refresh", err);
      setUser(null);
    } finally {
      setIsLoading(false);
      refreshingRef.current = false;
    }
  }, [tryRefreshTokens]);

  // A session that cannot be refreshed is a SIGN-IN problem, and the remedy is
  // signing in — not the permission-shaped "Access restricted" each view would
  // otherwise render for it. Dropping the user here lets ProtectedRoute do the
  // one thing that actually helps.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    // Migration: clear any previously stored localStorage tokens
    localStorage.removeItem("hub_access_token");
    localStorage.removeItem("hub_refresh_token");

    refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authService.login(email, password);
    if (response.user) {
      setUser(response.user);
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear in-memory tokens
    clearTokens();
    await authService.logout();
    setUser(null);
  }, []);

  const validateSession = useCallback(async (): Promise<boolean> => {
    const currentAccessToken = getAccessToken();
    if (!currentAccessToken) {
      return false;
    }
    try {
      const response = await authClient.validateToken({
        token: currentAccessToken,
      });
      return response.valid === true;
    } catch {
      return false;
    }
  }, []);

  const extendSession = useCallback(async (): Promise<boolean> => {
    const resolvedUser = await tryRefreshTokens();
    return resolvedUser !== null;
  }, [tryRefreshTokens]);

  const value: AuthContextType = {
    isAuthenticated: !!user,
    isLoading,
    user,
    login,
    logout,
    refresh,
    validateSession,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
